// ============================================================================
// Guardia de rutas derivada del catálogo de derechos (épica #662, #649).
// ============================================================================
// En vez de envolver rutas a mano, cada ruta se asocia con el nodo del catálogo
// que la posee, a partir del campo `routes` de cada app (su ruta de entrada):
//
//  1. Exacta o por debajo: si la ruta ES la de entrada de una app, o contiene
//     rutas de entrada de apps (`/pos/cashier` ⊃ `/pos/cashier/terminal`):
//     una sola app → esa app; varias de la MISMA categoría → la categoría Y,
//     además, cada hija con la de su app (una licencia puede traer sólo algunas);
//     de categorías distintas → ambigua: no se gobierna entera y se BAJA a sus
//     hijas (`/crm`, `/nuvex-access`, `/dashboards`).
//  2. Dentro: si la ruta cae dentro de la ruta de entrada de una app
//     (`/dashboards/cobranza` ⊂ `/dashboards`) → la más específica.
//  3. Hermana: las subpáginas suelen ser hermanas de la de entrada, no hijas
//     (`/crm/panel/actividad` junto a `/crm/panel/dashboard`). Si comparte al
//     menos dos segmentos con la ruta de entrada de UNA sola app → esa app.
//
// Parámetros (`/crm/contactos/:id`): cuenta el prefijo estático. El índice de
// una ruta ambigua lo gobierna la app cuya ruta de entrada es exactamente ésa.
// Los nodos retirados no cuentan: no se bloquea una pantalla viva sólo porque
// un id viejo la nombraba.
// ============================================================================
import type { RouteObject } from "react-router-dom";
import { type EntitlementNode } from "@torre/entitlements";
import { CATALOG } from "@/config/entitlementsCatalog";
import EntitlementRoute from "./EntitlementRoute";

/**
 * Rutas que NO se gobiernan por su app aunque el catálogo las asocie, con el
 * motivo. Excluirlas excluye también a sus hijas. P. ej. `/admin`
 * (Administración de Flotilla) aloja pantallas transversales — usuarios,
 * notificaciones, sesiones, avisos — a las que se llega desde otras partes (una
 * notificación sin URL, el asistente IA). Cerrarla entera por licencia dejaría
 * fuera a tenants sin flota. Se gobierna por menú cuando esas pantallas se
 * separen.
 */
const UNGATED_PATHS: Record<string, string> = {
  // Cobranza (CC-CONSOLE #1264): su guardia es CobranzaOperatorRoute. A plataforma le
  // aplica ahí mismo el derecho `app:cobranza`; al rol finance lo decide su rol.
  "/cobranza": "Cobranza: la guarda CobranzaOperatorRoute (derechos para plataforma, rol para finance)",
  "/admin": "pantallas transversales (usuarios, notificaciones, sesiones)",
  // El panel lateral del asistente IA es para todos, y su botón "pantalla
  // completa" abre /ai. La app "IA" del catálogo es interna, pero esta ruta no.
  "/ai": "pantalla completa del asistente IA, abierta a todos desde su panel",
  // Ayuda (wiki + tutoriales) es para toda persona con sesión: el botón "?" y
  // los tutoriales enlazan aquí. La licencia de "Ayuda y Tutoriales" sólo
  // decide su tile en el Hub, no si se puede leer la ayuda. Su BANDERA sí la
  // cierra, con su propia guardia (Layouts/help/helpFlag).
  "/ayuda": "wiki y tutoriales, abiertos a todo usuario con sesión",
};

const segmentsOf = (p: string) => p.split("/").filter(Boolean);
const trimSlash = (p: string) => p.replace(/\/+$/, "") || "/";

/** Prefijo estático: hasta antes del primer segmento con parámetro. */
function staticPrefix(path: string): string | null {
  if (!path.startsWith("/") || path.includes("*")) return null;
  const all = segmentsOf(path);
  const segs: string[] = [];
  for (const seg of all) {
    if (seg.startsWith(":")) break;
    segs.push(seg);
  }
  // Con parámetro sólo se decide si queda un prefijo con cuerpo (≥ 2 segmentos):
  // `/shop/:slug` o `/espacios/:slug` no se asocian a nada.
  if (segs.length < all.length && segs.length < 2) return null;
  return segs.length ? `/${segs.join("/")}` : null;
}

const isUngated = (path: string) => {
  const p = trimSlash(path);
  return Object.keys(UNGATED_PATHS).some((u) => p === u || p.startsWith(`${u}/`));
};

type AppRoute = { node: EntitlementNode; route: string };

function appRoutes(catalog: readonly EntitlementNode[]): AppRoute[] {
  return catalog
    .filter((n) => n.kind === "app" && n.status !== "deprecated")
    .flatMap((node) => (node.routes ?? []).map((route) => ({ node, route: trimSlash(route) })));
}

/** Una sola app → su llave; varias de la misma categoría → la categoría; si no, null. */
function ownerOf(nodes: EntitlementNode[]): string | null {
  const unique = [...new Map(nodes.map((n) => [n.key, n])).values()];
  if (unique.length === 0) return null;
  if (unique.length === 1) return unique[0].key;
  const parents = new Set(unique.map((n) => n.parent));
  return parents.size === 1 ? [...parents][0] : null;
}

/** Ruta → llave que la gobierna, o `null` (ambigua, sin dueño o excluida). */
export function entitlementKeyForPath(
  path: string | undefined,
  catalog: readonly EntitlementNode[] = CATALOG,
): string | null {
  if (!path || path === "/" || isUngated(path)) return null;
  const prefix = staticPrefix(path);
  if (!prefix || prefix === "/") return null;
  const routes = appRoutes(catalog);

  // 1. Exacta o por debajo.
  const within = routes.filter((r) => r.route === prefix || r.route.startsWith(`${prefix}/`));
  if (within.length) return ownerOf(within.map((r) => r.node));

  // 2. Dentro de la ruta de entrada de una app: gana la más específica.
  const containing = routes.filter((r) => prefix.startsWith(`${r.route}/`));
  if (containing.length) {
    const longest = Math.max(...containing.map((r) => r.route.length));
    return ownerOf(containing.filter((r) => r.route.length === longest).map((r) => r.node));
  }

  // 3. Hermana: comparte ≥ 2 segmentos con la ruta de entrada de una app.
  const segs = segmentsOf(prefix);
  const shared = (route: string) => {
    const other = segmentsOf(route);
    let n = 0;
    while (n < segs.length && n < other.length && segs[n] === other[n]) n += 1;
    return n;
  };
  const scored = routes.map((r) => ({ ...r, shared: shared(r.route) })).filter((r) => r.shared >= 2);
  if (!scored.length) return null;
  const best = Math.max(...scored.map((r) => r.shared));
  return ownerOf(scored.filter((r) => r.shared === best).map((r) => r.node));
}

/** App cuya ruta de entrada es EXACTAMENTE `path` (para el índice de una ruta ambigua). */
function exactOwner(path: string, catalog: readonly EntitlementNode[]): string | null {
  return ownerOf(appRoutes(catalog).filter((r) => r.route === trimSlash(path)).map((r) => r.node));
}

const joinPath = (parent: string, child: string | undefined) => {
  if (!child) return parent;
  if (child.startsWith("/")) return child;
  return `${parent.replace(/\/+$/, "")}/${child}`;
};

const wrap = (route: RouteObject, key: string): RouteObject => {
  const { path, ...inner } = route;
  // Con hijos, lo original queda como layout sin ruta; sin hijos, como la ruta
  // índice (un layout sin hijos nunca coincide con la URL).
  const child = (inner.children?.length ? inner : { ...inner, index: true }) as RouteObject;
  return { path, element: <EntitlementRoute entitlement={key} />, children: [child] };
};

function gateRoute(
  route: RouteObject,
  parentPath: string,
  catalog: readonly EntitlementNode[],
): RouteObject {
  if (route.index) {
    // Llegamos aquí bajo una madre ambigua (sin guardia propia) o gobernada por su
    // categoría. Si una app tiene EXACTAMENTE esta ruta de entrada, la protege.
    const key = parentPath && !isUngated(parentPath) ? exactOwner(parentPath, catalog) : null;
    return key
      ? ({
          ...route,
          element: <EntitlementRoute entitlement={key}>{route.element}</EntitlementRoute>,
        } as RouteObject)
      : route;
  }
  const fullPath = joinPath(parentPath, route.path);
  // Excluida a propósito: ni ella ni sus hijas.
  if (isUngated(fullPath)) return route;
  const key = route.path ? entitlementKeyForPath(fullPath, catalog) : null;
  if (key) {
    // Gobernada por su CATEGORÍA (varias apps de la misma comparten el prefijo,
    // p. ej. /cargo-market): la categoría no basta, porque una licencia puede
    // traer sólo algunas de sus apps. Se baja también a las hijas para aplicar
    // la guardia de cada app.
    const inner =
      key.startsWith("cat:") && route.children?.length
        ? { ...route, children: route.children.map((c) => gateRoute(c, fullPath, catalog)) }
        : route;
    return wrap(inner, key);
  }
  // Ambigua o sin dueño: se baja a sus hijas, que pueden tener dueño único.
  if (route.children?.length) {
    return { ...route, children: route.children.map((c) => gateRoute(c, fullPath, catalog)) };
  }
  return route;
}

/**
 * Envuelve cada ruta que el catálogo gobierna con su `EntitlementRoute`: la de
 * primer nivel si tiene dueño; si es ambigua, sus hijas. Lo demás queda intacto.
 */
export function withEntitlementGates(
  routes: RouteObject[],
  catalog: readonly EntitlementNode[] = CATALOG,
): RouteObject[] {
  return routes.map((route) => gateRoute(route, "", catalog));
}
